import React, {Fragment, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {useFormikContext} from 'formik';
import {noop} from 'lodash';

import {Course, Store} from 'src/types/store';
import {courseGroupingKeyFromInstitution, newCourseObject} from './utils';

import CollapsibleSection from 'src/components/collapsible-section';
import Icon from 'shared/components/icon';
import Button from 'src/components/button';
import TextButton from 'src/components/TextButton';
import SchoolHeader from './school-header';
import {CourseForm} from './course-form-wrapper';
import InlineCourseForm from './inline-course-form';
import styles from './editing-school-section.module.scss';
import classnames from 'classnames';
import {useBoolean} from '../../../src/hooks/useBoolean';
import {Keyboard} from '@capacitor/keyboard';
import PlatformService from '../../../src/services/PlatformService';

interface Props {
  institutionId: string;
  courseGroups: Record<string, Course[]>;
  numberInstitutionsWithCourses: number;
  year: string;
}

const MobileEditingSchoolSection = ({
  institutionId,
  courseGroups,
  numberInstitutionsWithCourses,
  year,
}: Props) => {
  const history = useHistory();
  const {pathname} = useLocation();
  const institution = useSelector((state: Store) => state.institutions[institutionId]);
  const formik = useFormikContext<Record<string, CourseForm[]>>();
  const multipleInstitutions = numberInstitutionsWithCourses > 1;
  const [open, setOpen] = useState(!multipleInstitutions);
  const coursesByGradeSettings = Object.keys(courseGroups);
  const groupingKeyFromInstitution = JSON.stringify(courseGroupingKeyFromInstitution(institution));
  const nativeKeyboardIsOpen = useBoolean(false);

  useEffect(() => {
    Keyboard.addListener('keyboardDidShow', nativeKeyboardIsOpen.setTrue);
    Keyboard.addListener('keyboardDidHide', nativeKeyboardIsOpen.setFalse);

    return () => {
      Keyboard.removeAllListeners();
    };
  }, []);

  const header = (
    <SchoolHeader>
      {institution.name}

      {open && (
        <div
          onClick={() =>
            history.replace({
              pathname: `${pathname}/school/${institutionId}`,
              state: {skipScrolling: true},
            })
          }
          className={styles.mobilePenButton}
          aria-label={`Edit ${institution.name}`}
          data-testid="mobile-bulk-entry-edit-school"
        >
          <Icon iconType="pen" className={styles.mobilePen} />
        </div>
      )}
    </SchoolHeader>
  );

  const content = (
    <div
      className={classnames(styles.mobileContent, {
        [styles.nativeFirstCourseSpacing]:
          coursesByGradeSettings.length < 2 &&
          nativeKeyboardIsOpen.value &&
          PlatformService.runtime() === 'ios',
      })}
    >
      <Button
        outline
        onClick={() =>
          formik.setFieldValue(groupingKeyFromInstitution, [
            {...newCourseObject(institution), schoolYear: year},
            ...formik.values[groupingKeyFromInstitution],
          ])
        }
        margin={false}
        size="small"
        width="100%"
        testId="mobile-bulk-entry-add-course"
      >
        + Add Course
      </Button>

      {coursesByGradeSettings.map(
        (gradeSettingsStr) =>
          formik.values[gradeSettingsStr]?.map((course, i) => (
            <Fragment key={course._id}>
              <InlineCourseForm
                hideLabels={i !== 0}
                prefix={`${gradeSettingsStr}.${i}.`}
                removeCourse={noop}
              />

              <TextButton
                onClick={() =>
                  history.replace({
                    pathname: `${pathname}/course/${course.new ? 'new' : course._id}`,
                    state: {skipScrolling: true, ...(course.new && {initialValues: course})},
                  })
                }
              >
                Advanced course settings
              </TextButton>
            </Fragment>
          ))
      )}
    </div>
  );

  return multipleInstitutions ? (
    <CollapsibleSection
      title={header}
      isOpen={false}
      className={styles.mobileSection}
      onEnter={() => setOpen(!open)}
    >
      {content}
    </CollapsibleSection>
  ) : (
    <>
      <div className="mb-12">{header}</div>
      {content}
    </>
  );
};

export default MobileEditingSchoolSection;
